import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { dsSnapshotService } from '@services/dsSnapshotService';
import { dsFileService } from '@services/dsFileService';
import { OnlyOfficeEditor } from '@workpay/ds-shared/components/OnlyOfficeEditor';
import { PdfViewer } from '@workpay/ds-shared/components/PdfViewer';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { useAuthStore } from '@stores/authStore';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import { getLegalEntities } from '@services/legalEntityService';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import { toTitleCase } from '@utils/textCase';
import { STATUS_CLASS_MAP, FORMAT_CLASS_MAP } from './snapshotBadges';
import { ReviewerView } from './reviewer/ReviewerView';
import styles from './SnapshotDetail.module.scss';

interface SnapshotDetailProps {
  snapshotId: string;
  initialReview?: boolean;
}

export function SnapshotDetail({ snapshotId, initialReview = false }: SnapshotDetailProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);

  // Draggable split — throttled with rAF
  const [splitPercent, setSplitPercent] = useState(40);
  const [isExpanded, setIsExpanded] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isExpanded]);

  const handleSplitStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;

    let rafId = 0;
    const onMouseMove = (ev: MouseEvent) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        setSplitPercent(Math.max(25, Math.min(75, pct)));
      });
    };
    const onMouseUp = () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const { data: snapshot, isLoading, error: snapshotErrorObj } = useQuery({
    queryKey: ['dsSnapshot', snapshotId],
    queryFn: () => dsSnapshotService.getById(snapshotId),
  });
  const snapshotForbidden = isForbiddenError(snapshotErrorObj);

  // Set dynamic breadcrumb
  useEffect(() => {
    if (snapshot?.name) {
      setOverride(`/t/contracts/${snapshotId}`, snapshot.name);
      return () => setOverride(`/t/contracts/${snapshotId}`, null);
    }
  }, [snapshot?.name, snapshotId, setOverride]);

  // Resolve classification IDs to names
  const { countries } = useCountries();
  const countryName = countries.find((c) => c.id === snapshot?.countryId)?.name;

  const { data: legalEntitiesData } = useQuery({
    queryKey: ['legalEntities', { countryId: snapshot?.countryId, size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }, { filters: { countryId: snapshot!.countryId! } }),
    enabled: !!snapshot?.countryId,
  });
  const legalEntityName = legalEntitiesData?.content?.find((le) => le.id === snapshot?.legalEntityId)?.name;

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });
  const employmentTypeName = employmentTypesData?.content?.find((et) => et.id === snapshot?.employmentTypeId)?.name;

  const { data: documentTypesData } = useQuery({
    queryKey: ['documentTypesAll', { size: 100 }],
    queryFn: () => getDocumentTypes({ size: 100 }),
  });
  const documentTypeName = documentTypesData?.content?.find((dt) => dt.id === snapshot?.documentTypeId)?.name;

  // Review mode: IN_REVIEW + permission + review param. REVIEW_COMPLETED is the
  // post-dual-completion parking state — review session is over.
  const canReview = hasPermission('DOCUMENT_SNAPSHOTS_UPDATE') && snapshot?.status === 'IN_REVIEW';
  const isReviewing = initialReview && canReview;
  // Display label for snapshot.status. IN_REVIEW / REVIEW_COMPLETED need explicit
  // copy; everything else falls through to toTitleCase ("ACTIVE" → "Active").
  const statusLabel = !snapshot
    ? ''
    : snapshot.status === 'IN_REVIEW'
      ? t('K_SNAPSHOT_STATUS_IN_REVIEW', 'In Review')
      : snapshot.status === 'REVIEW_COMPLETED'
        ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')
        : toTitleCase(snapshot.status);

  const isDocx = snapshot?.templateFormat === 'DOCX';
  const isPdf = snapshot?.templateFormat === 'PDF';

  // Fetch snapshot-scoped editor config for BOTH view and review modes — always pass
  // it to OnlyOfficeEditor via `configOverride` so the component does NOT fall back
  // to the master-template endpoint (/v1/onlyoffice/editor-config) which tenants
  // lack permission for (403). Backend returns mode-appropriate permissions + docKey.
  // staleTime: Infinity prevents refetch-triggered editor reinit (#3).
  const editorMode = isReviewing ? 'review' : 'view';
  const { data: editorConfig } = useQuery({
    queryKey: ['dsSnapshotEditorConfig', snapshotId, editorMode],
    queryFn: () => dsSnapshotService.getEditorConfig(snapshotId, editorMode),
    enabled: !!snapshot?.sourceFileId && isDocx,
    staleTime: Infinity,
  });
  const pdfBlobQuery = useQuery({
    queryKey: ['snapshotPdfBlob', snapshotId, snapshot?.sourceFileId],
    queryFn: async () => {
      const res = await dsFileService.download(snapshot!.sourceFileId);
      return URL.createObjectURL(res.data);
    },
    enabled: !!snapshot?.sourceFileId && isPdf && !isReviewing,
    staleTime: Infinity,
  });

  const pdfUrl = pdfBlobQuery.data;
  const pdfLoading = pdfBlobQuery.isLoading;

  // Revoke the PREVIOUS blob URL when a new one arrives. Do NOT revoke on unmount —
  // the query cache (staleTime: Infinity) holds this URL and serves it on next mount,
  // so unmount-revoke would cause "Failed to load PDF" on re-entry.
  const prevPdfUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevPdfUrlRef.current && prevPdfUrlRef.current !== pdfUrl) {
      URL.revokeObjectURL(prevPdfUrlRef.current);
    }
    prevPdfUrlRef.current = pdfUrl;
  }, [pdfUrl]);

  // --- Forbidden (page-level — detail route can't render anything without the snapshot) ---
  if (snapshotForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={() => navigate({ to: '/t/contracts' })}
          actionLabel={t('K_BUTTON_GO_BACK', 'Go Back')}
        />
      </div>
    );
  }

  if (isLoading || !snapshot) {
    return <div className={styles.loading}>{t('K_SNAPSHOT_LOADING', 'Loading contract...')}</div>;
  }

  // Review mode short-circuits the entire view-mode layout. ReviewerView owns
  // the header, OnlyOffice mount, chat, activity, SSE — everything. The
  // useQuery calls above (editorConfig, pdfBlobQuery) are effectively no-ops
  // here: editorConfig shares the `['dsSnapshotEditorConfig', id, 'review']`
  // key with useReviewerSession's query inside ReviewerView (TanStack dedupes
  // to a single request); pdfBlobQuery is disabled because `!isReviewing` is
  // in its `enabled` clause.
  if (isReviewing) {
    return <ReviewerView snapshotId={snapshotId} snapshot={snapshot} backTo="/t/contracts" />;
  }

  const expandLabel = isExpanded ? t('K_DS_EXIT_FULLSCREEN', 'Exit fullscreen') : t('K_DS_FULLSCREEN', 'Fullscreen');

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate({ to: '/t/contracts' })}>
            {'\u2190'} {t('K_BREADCRUMB_CONTRACTS', 'Contracts')}
          </button>
          <h1 className={styles.title}>{snapshot.name}</h1>
          <span className={`${styles.badge} ${styles[STATUS_CLASS_MAP[snapshot.status] ?? 'badgeNeutral'] ?? ''}`}>
            {statusLabel}
          </span>
          <span className={`${styles.badge} ${styles[FORMAT_CLASS_MAP[snapshot.templateFormat] ?? 'badgeNeutral'] ?? ''}`}>
            {snapshot.templateFormat}
          </span>
          {isReviewing && (
            <span className={`${styles.badge} ${styles.badgeWarning ?? ''}`}>
              {t('K_SNAPSHOT_REVIEWING', 'Reviewing')}
            </span>
          )}
        </div>
        {/* Only render header right when there's content (#9) */}
        {!isReviewing && canReview && (
          <div className={styles.headerRight}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: '/t/contracts/$snapshotId', params: { snapshotId }, search: { review: true } })}
            >
              {t('K_REVIEW', 'Review')}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.splitContainer} ref={splitContainerRef}>
        {/* Left panel: metadata */}
        <div className={styles.leftPanel} style={{ width: `${splitPercent}%` }}>
          <div className={styles.leftPanelScroll}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('K_SNAPSHOT_DETAIL_DETAILS', 'Details')}</h2>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_NAME', 'Name')}</span>
                  <span className={styles.detailValue}>{snapshot.name}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_DESCRIPTION', 'Description')}</span>
                  <span className={styles.detailValue}>{snapshot.description || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_COUNTRY', 'Country')}</span>
                  <span className={styles.detailValue}>{countryName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_LEGAL_ENTITY', 'Legal Entity')}</span>
                  <span className={styles.detailValue}>{legalEntityName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}</span>
                  <span className={styles.detailValue}>{employmentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_LABEL_DOCUMENT_TYPE', 'Document Type')}</span>
                  <span className={styles.detailValue}>{documentTypeName || '\u2014'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_LABEL_FORMAT', 'Format')}</span>
                  <span className={styles.detailValue}>{snapshot.templateFormat}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_STATUS', 'Status')}</span>
                  <span className={styles.detailValue}>
                    {statusLabel}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_DS_COLUMN_PAGES', 'Pages')}</span>
                  <span className={styles.detailValue}>{snapshot.pageCount || 'N/A'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t('K_COLUMN_CREATED', 'Created')}</span>
                  <span className={styles.detailValue}>{new Date(snapshot.createdOn).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Draggable split handle */}
        <div className={styles.splitHandle} onMouseDown={handleSplitStart} />

        {/* Right panel: document viewer / editor */}
        <div className={`${styles.rightPanel} ${isExpanded ? styles.rightPanelExpanded : ''}`}>
          <div className={styles.previewToolbar}>
            <span className={styles.previewToolbarTitle}>{snapshot.name}</span>
            <span className={styles.previewToolbarMeta}>
              {isReviewing && `${t('K_SNAPSHOT_REVIEWING', 'Reviewing')} \u00B7 `}
              {snapshot.pageCount} {snapshot.pageCount === 1
                ? t('K_DS_PAGE', 'page')
                : t('K_DS_PAGES', 'pages')}
            </span>
            {/* #7: aria-label for screen readers */}
            <button
              className={styles.expandBtn}
              onClick={() => setIsExpanded((v) => !v)}
              aria-label={expandLabel}
              title={expandLabel}
            >
              {isExpanded ? '\u2296' : '\u2295'}
            </button>
          </div>
          <div className={styles.previewContent}>
            {isReviewing && editorConfig ? (
              <OnlyOfficeEditor
                fileId={snapshot.sourceFileId}
                mode="edit"
                configOverride={editorConfig}
              />
            ) : isDocx && snapshot.sourceFileId && editorConfig ? (
              <OnlyOfficeEditor
                fileId={snapshot.sourceFileId}
                mode="view"
                configOverride={editorConfig}
              />
            ) : isPdf ? (
              pdfLoading ? (
                <div className={styles.pdfLoader}>
                  <div className={styles.spinner} />
                  {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
                </div>
              ) : pdfUrl ? (
                <div className={styles.pdfViewerWrap}>
                  <PdfViewer
                    url={pdfUrl}
                    pageNumber={1}
                    fields={[]}
                    allFields={[]}
                    selectedFieldIndex={null}
                    onFieldSelect={() => {}}
                    onFieldMove={() => {}}
                    zoom={1}
                    onDocumentLoad={() => {}}
                    onVisiblePageChange={() => {}}
                  />
                </div>
              ) : (
                <div className={styles.pdfLoader}>
                  {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
                </div>
              )
            ) : (
              <div className={styles.pdfLoader}>
                {t('K_DS_LOADING_PREVIEW', 'Loading preview...')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
